import {compact} from '@benchling/js-utils';
import React from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import StepDocumentLink from 'notebook/worksheets/steps/StepDocumentLink';
import {NO_ACCESS_TEXT} from 'processes/ProcessConstants';
import {ChipSize} from 'ui-library/chips/Chip';
import {ChipPopoverInfoField} from 'ui-library/chips/ChipPopoverContent';
import ProcessTaskChip from 'ui-library/chips/ProcessTaskChip';

import {GetChipPopoverContentsArgs} from '../ChipUtilConstants';
import {
  getAssigneeField,
  getIdField,
  getNameField,
  getScheduledOnField,
  getStatusField,
  getStudiesField,
} from '../getDefaultProcessTaskChipPopoverContent';
import {ProcessTaskForPopoverGql} from '../graphql';
import {getProcedureChipPopoverFields} from './getCommonFields';

type ProcessTaskStepExecutionInstanceOrInaccessibleGql =
  ProcessTaskForPopoverGql['procedureStepExecutionInstance'];
type ProcessTaskStepExecutionInstanceGql = ExtractByTypename<
  ProcessTaskForPopoverGql['procedureStepExecutionInstance'],
  'ProcedureStepExecutionInstanceNode'
>;
type ProcessTaskStepExecutionInstanceMethodExecutionInstanceGql = ExtractByTypename<
  ProcessTaskStepExecutionInstanceGql['methodExecutionInstance'],
  'ProcedureMethodExecutionInstanceNode'
>;
type ProcessTaskStepExecutionInstanceDocumentGql = ExtractByTypename<
  ProcessTaskStepExecutionInstanceGql['document'],
  'DocumentNode'
>;

export function extractStepExecutionInfo(
  stepExecutionInstance: ProcessTaskStepExecutionInstanceOrInaccessibleGql | null
): {
  methodExecutionInstance: ProcessTaskStepExecutionInstanceMethodExecutionInstanceGql | null;
  stepDocument: ProcessTaskStepExecutionInstanceDocumentGql | null;
  conditionReplicateRunTaskId: string | null;
  methodTaskComponent: string | JSX.Element;
} {
  let methodExecutionInstance = null;
  let stepDocument = null;
  if (
    stepExecutionInstance != null &&
    !isInaccessible(stepExecutionInstance) &&
    !isInaccessible(stepExecutionInstance.document)
  ) {
    methodExecutionInstance = stepExecutionInstance?.methodExecutionInstance;
    stepDocument = stepExecutionInstance?.document;
  }
  const conditionReplicateRunTask = methodExecutionInstance?.procedureRunConditionReplicate?.processRootTask;
  const conditionReplicateRunTaskId =
    conditionReplicateRunTask != null ? getAccessibleOrInaccessibleId(conditionReplicateRunTask) : null;
  let methodTaskComponent: string | JSX.Element;
  if (methodExecutionInstance == null) {
    methodTaskComponent = 'N/A';
  } else if (methodExecutionInstance.methodTask == null) {
    methodTaskComponent = isInaccessible(methodExecutionInstance.templateMethodVersion)
      ? NO_ACCESS_TEXT
      : methodExecutionInstance.templateMethodVersion.name;
  } else {
    const methodTaskId = getAccessibleOrInaccessibleId(methodExecutionInstance.methodTask);
    methodTaskComponent = <ProcessTaskChip id={methodTaskId} size={ChipSize.MINI} />;
  }

  return {
    methodExecutionInstance,
    stepDocument,
    conditionReplicateRunTaskId,
    methodTaskComponent,
  };
}

export default function getProcedureStepChipPopoverContents({
  processTask,
  icon,
  runPopoverField,
  unitOpPopoverField,
  procedureTemplatePopoverField,
}: GetChipPopoverContentsArgs & {
  runPopoverField: ChipPopoverInfoField;
  unitOpPopoverField: ChipPopoverInfoField;
  procedureTemplatePopoverField: ChipPopoverInfoField;
}): Array<ChipPopoverInfoField> {
  const stepExecutionInstance = processTask.procedureStepExecutionInstance;

  const {stepDocument} = extractStepExecutionInfo(stepExecutionInstance);

  return compact([
    getIdField(processTask),
    getNameField(processTask, icon),
    getStatusField(processTask),
    getAssigneeField(processTask),
    getScheduledOnField(processTask),
    {
      label: getProcedureChipPopoverFields().WORKSHEET,
      value:
        stepDocument != null ? (
          <StepDocumentLink href={stepDocument.editUrl}>{stepDocument.name}</StepDocumentLink>
        ) : (
          NO_ACCESS_TEXT
        ),
    },
    unitOpPopoverField,
    runPopoverField,
    procedureTemplatePopoverField,
    getStudiesField(processTask),
  ]);
}
